import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { VmManagementService, VirtualMachine } from '../../../services/vm-management.service';
import { ProxmoxService } from '../../../services/proxmox.service';
import { ClientManagementService, Client } from '../../../services/client-management.service';

@Component({
  selector: 'app-client-dashboard',
  templateUrl: './client-dashboard.component.html',
  styleUrls: ['./client-dashboard.component.scss']
})
export class ClientDashboardComponent implements OnInit {
  clientVMs: VirtualMachine[] = [];
  client: Client | undefined;
  loading: boolean = false;
  error: string = '';
  successMessage: string = '';
  clientId: string = '';
  loggedInUsername: string = '';

  // Stats
  totalVMs: number = 0;
  runningVMs: number = 0;
  stoppedVMs: number = 0;
  pausedVMs: number = 0;

  constructor(
    private vmService: VmManagementService,
    private proxmoxService: ProxmoxService,
    private clientService: ClientManagementService,
    private router: Router
  ) { }

  ngOnInit(): void {
    // Get client ID from session storage
    const clientUsername = sessionStorage.getItem('clientUser');
    this.loggedInUsername = clientUsername || '';
    
    if (clientUsername) {
      // Find client by username (in a real app, this would be done via API)
      this.clientService.getClients().subscribe(clients => {
        const foundClient = clients.find(c => c.proxmoxUser === clientUsername);
        if (foundClient) {
          this.client = foundClient;
          this.clientId = foundClient.id;
          this.loadClientData();
        } else {
          // If client not found by proxmoxUser, try to find by name or show a more detailed error
          this.error = `Client with username '${clientUsername}' not found in the system. Please contact administrator.`;
          console.error('Client not found for username:', clientUsername);
          console.log('Available clients:', clients);
        }
      });
    } else {
      // Redirect to login if no client info
      this.router.navigate(['/client-login']);
    }
  }

  loadClientData(): void {
    this.loading = true;
    this.error = '';
    this.successMessage = '';

    // Load client VMs
    this.vmService.getVMsByClientId(this.clientId).subscribe({
      next: (vms) => {
        this.clientVMs = vms;
        this.totalVMs = vms.length;
        this.runningVMs = vms.filter(vm => vm.status === 'running').length;
        this.stoppedVMs = vms.filter(vm => vm.status === 'stopped').length;
        this.pausedVMs = vms.filter(vm => vm.status === 'paused').length;
        this.loading = false;
      },
      error: (error) => {
        console.error('Error loading client VMs:', error);
        this.error = 'Error loading VMs. Please try again.';
        this.loading = false;
      }
    });
  }

  onViewVM(vmId: string): void {
    this.router.navigate(['/vm-details', vmId]);
  }

  onAddVM(): void {
    // Navigate to add VM page
    this.router.navigate(['/add-vm']);
  }

  onStartVM(vmId: string): void {
    this.vmService.startVM(vmId).subscribe({
      next: (response) => {
        if (response.success) {
          this.successMessage = response.message;
          this.loadClientData(); // Refresh data
          setTimeout(() => this.successMessage = '', 3000);
        } else {
          this.error = response.message;
        }
      },
      error: (error) => {
        console.error('Error starting VM:', error);
        this.error = 'Error starting VM. Please try again.';
      }
    });
  }

  onStopVM(vmId: string): void {
    this.vmService.stopVM(vmId).subscribe({
      next: (response) => {
        if (response.success) {
          this.successMessage = response.message;
          this.loadClientData(); // Refresh data
          setTimeout(() => this.successMessage = '', 3000);
        } else {
          this.error = response.message;
        }
      },
      error: (error) => {
        console.error('Error stopping VM:', error);
        this.error = 'Error stopping VM. Please try again.';
      }
    });
  }

  onRestartVM(vmId: string): void {
    this.vmService.restartVM(vmId).subscribe({
      next: (response) => {
        if (response.success) {
          this.successMessage = response.message;
          this.loadClientData(); // Refresh data
          setTimeout(() => this.successMessage = '', 3000);
        } else {
          this.error = response.message;
        }
      },
      error: (error) => {
        console.error('Error restarting VM:', error);
        this.error = 'Error restarting VM. Please try again.';
      }
    });
  }

  refreshData(): void {
    this.loadClientData();
  }

  getVMStatusClass(status: string): string {
    switch (status) {
      case 'running':
        return 'badge-success';
      case 'stopped':
        return 'badge-danger';
      case 'paused':
        return 'badge-warning';
      default:
        return 'badge-secondary';
    }
  }
}